// Shared mental state calculation functions used by DoctorDashboard, HospitalDashboard,
// and Hospital_DoctorDashboard

/**
 * Calculate overall mental health score from emotions.
 * @param {Object} emotions - Object with emotion values (joy, stress, sadness, anger, hurt, burnout_score)
 * @returns {number} Score between 0 and 100
 */
export const calculateOverallMentalHealthScore = (emotions) => {
  if (!emotions) return 0;
  const joy = Number(emotions.joy) || 0;
  const stress = Number(emotions.stress) || 0;
  const sadness = Number(emotions.sadness) || 0;
  const anger = Number(emotions.anger) || 0;
  const hurt = Number(emotions.hurt) || 0;
  const burnout = Number(emotions.burnout_score) || 0;
  if (joy === 0 && stress === 0 && sadness === 0 && anger === 0 && hurt === 0 && burnout === 0) return 0;
  const positiveWeight = joy * 0.4;
  const negativeWeight = (stress + sadness + anger + hurt + burnout) * 0.12;
  return Math.max(0, Math.min(100, Math.round(50 + positiveWeight - negativeWeight)));
};

/**
 * Calculate mental state for a patient based on emotion vector, reports, risk, and overall mark.
 * @param {Object} patient - Patient object with emotionVector, weeklyReport, monthlyReport, overallMark, cumulativeRisk, riskComponents
 * @returns {Object} { state, color, summary, score }
 */
export const calculateMentalState = (patient) => {
  const emotionWeights = { joy: 0.4, stress: -0.3, sadness: -0.2, anger: -0.2, hurt: -0.1 };
  const emotionScore = Object.entries(patient.emotionVector || {}).reduce((score, [emotion, value]) => {
    const weight = emotionWeights[emotion] || 0;
    const numValue = Number(value) || 0;
    return score + ((numValue / 100) * weight);
  }, 0);

  const weeklyReport = Array.isArray(patient.weeklyReport) ? patient.weeklyReport : [];
  const weeklyTrend = weeklyReport.length >= 2
    ? (typeof weeklyReport[weeklyReport.length - 1] === 'object'
      ? weeklyReport[weeklyReport.length - 1].score
      : weeklyReport[weeklyReport.length - 1]) -
    (typeof weeklyReport[0] === 'object'
      ? weeklyReport[0].score
      : weeklyReport[0])
    : 0;

  const monthlyReport = Array.isArray(patient.monthlyReport) ? patient.monthlyReport : [];
  const monthlyTrend = monthlyReport.length >= 2
    ? (typeof monthlyReport[monthlyReport.length - 1] === 'object'
      ? monthlyReport[monthlyReport.length - 1].score
      : monthlyReport[monthlyReport.length - 1]) -
    (typeof monthlyReport[0] === 'object'
      ? monthlyReport[0].score
      : monthlyReport[0])
    : 0;

  const baseScore = Number(patient.overallMark) || 70;
  const trendAdjustment = (Number(weeklyTrend) || 0) * 0.3 + (Number(monthlyTrend) || 0) * 0.2;
  const emotionAdjustment = (Number(emotionScore) || 0) * 100;
  const riskPenalty = (Number(patient.cumulativeRisk) || 0) * 0.3;
  const finalScore = Math.max(0, Math.min(100, baseScore + trendAdjustment + emotionAdjustment - riskPenalty));

  if (isNaN(finalScore) || !isFinite(finalScore)) {
    return { score: 50, state: "Unknown", color: "#6b7280", summary: "Unable to calculate mental state." };
  }

  const burnout = patient.riskComponents?.burnout || 0;
  let state, color, summary;
  if (finalScore >= 70 && burnout < 30) {
    state = "Healthy";
    color = "#28a745";
    summary = "Patient demonstrates excellent emotional well-being.";
  } else if (finalScore >= 50 && burnout < 50) {
    state = "Stable";
    color = "#17a2b8";
    summary = "Patient maintains stable mental state.";
  } else if (finalScore >= 30 && burnout < 70) {
    state = "Declining";
    color = "#fd7e14";
    summary = "Patient shows concerning signs with increased negative emotional indicators.";
  } else {
    state = "Critical";
    color = "#dc3545";
    summary = "Critical risk levels detected - immediate intervention recommended.";
  }

  return { state, color, summary, score: finalScore };
};
